import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Crown, Check, Zap, Star, Sparkles, Heart, Users, TreeDeciduous, Wallet } from 'lucide-react';
import { PageTransition, FadeInItem } from '@/components/motion/Animations';

const PLANS = [
  {
    id: 'free',
    name: 'Free',
    subtitle: 'Padrão',
    price: 'Grátis',
    period: '',
    description: 'Comece sua jornada com acesso ao essencial',
    stripeLink: null,
    icon: Users,
    color: 'text-slate-500',
    bg: 'bg-slate-100 dark:bg-slate-800/40',
    border: 'border-border/40',
    nft: false,
    popular: false,
    benefits: ['Feed Social', 'Check-in Diário', 'Grupos de Apoio', '5 Atividades Diárias', 'Planta do Dia básica', '1 Árvore Viva da plataforma'],
  },
  {
    id: 'premium_monthly',
    name: 'Premium Mensal',
    subtitle: 'Sem transferência de NFT',
    price: 'R$ 29,90',
    period: '/mês',
    description: 'Gamificação completa e sementes NFT',
    stripeLink: 'https://buy.stripe.com/6oUcN5dew8MP1jc7u38Vi06',
    icon: Zap,
    color: 'text-indigo-500',
    bg: 'bg-indigo-100 dark:bg-indigo-900/30',
    border: 'border-indigo-200 dark:border-indigo-800/40',
    nft: false,
    popular: false,
    benefits: ['10 Atividades Diárias', 'Badge Premium', 'Jardim Completo + IA', 'Sementes NFT', 'Insights e Padrões'],
  },
  {
    id: 'premium_quarterly',
    name: 'Premium Trimestral',
    subtitle: 'Dono da NFT na Wallet',
    price: 'R$ 79,90',
    period: '/3 meses',
    originalPrice: 'R$ 89,70',
    description: 'Economia de 11% — compromisso real',
    stripeLink: 'https://buy.stripe.com/28E28r1vO8MP4vobKj8Vi05',
    icon: Star,
    color: 'text-amber-500',
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    border: 'border-amber-200 dark:border-amber-800/40',
    nft: true,
    popular: true,
    benefits: ['Tudo do Mensal', '11% de economia', 'NFT própria na Carteira VSA', 'Reuniões prioritárias', 'Diário Profundo'],
  },
  {
    id: 'premium_annual',
    name: 'Premium Anual',
    subtitle: 'Dono da NFT na Wallet',
    price: 'R$ 249,90',
    period: '/ano',
    originalPrice: 'R$ 358,80',
    description: 'Melhor valor — 30% de economia',
    stripeLink: 'https://buy.stripe.com/8x23cvgqId353rk01B8Vi04',
    icon: Crown,
    color: 'text-violet-500',
    bg: 'bg-violet-100 dark:bg-violet-900/30',
    border: 'border-violet-200 dark:border-violet-800/40',
    nft: true,
    popular: false,
    benefits: ['Tudo do Trimestral', '30% de economia', 'NFT própria na Carteira VSA', 'Prioridade em novos recursos'],
  },
  {
    id: 'founder-90',
    name: 'Acompanhamento 90 dias',
    subtitle: 'Ganha NFT na Wallet',
    price: 'R$ 145,00',
    period: '',
    originalPrice: 'R$ 350,00',
    description: 'Metodologia Enantiodromia — Recuperação Profunda',
    stripeLink: 'https://buy.stripe.com/6oUfZhb6o0gje5Y15F8Vi01',
    icon: Sparkles,
    color: 'text-orange-500',
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    border: 'border-orange-200 dark:border-orange-800/40',
    nft: true,
    popular: false,
    benefits: ['Programa de Apoio completo', 'Metodologia Enantiodromia', 'Badge Fundador', '2x tokens AWA', 'NFT na Carteira VSA'],
  },
  {
    id: 'founder-180',
    name: 'Acompanhamento 180 dias',
    subtitle: 'Ganha NFT na Wallet',
    price: 'R$ 320,00',
    period: '',
    originalPrice: 'R$ 669,00',
    description: 'Melhor Custo-Benefício — Recuperação Completa',
    stripeLink: 'https://buy.stripe.com/cNi5kD3DWd352ng9Cb8Vi00',
    icon: Crown,
    color: 'text-amber-600',
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    border: 'border-amber-200 dark:border-amber-800/40',
    nft: true,
    popular: false,
    benefits: ['Tudo do 90 dias', 'MELHOR custo-benefício', 'Suporte prioritário', 'Grupos exclusivos', 'NFT na Carteira VSA'],
  },
  {
    id: 'course-family',
    name: 'Plano Familiar',
    subtitle: 'Ganha NFT na Wallet',
    price: 'R$ 250,00',
    period: '',
    description: '90 dias de programa estruturado para familiares',
    stripeLink: 'https://buy.stripe.com/00w28rdewd352ng5lV8Vi02',
    icon: Heart,
    color: 'text-pink-500',
    bg: 'bg-pink-100 dark:bg-pink-900/30',
    border: 'border-pink-200 dark:border-pink-800/40',
    nft: true,
    popular: false,
    benefits: ['Programa completo 90 dias', 'Técnicas de apoio familiar', 'Suporte emocional', 'Certificado de conclusão', 'NFT na Carteira VSA'],
  },
];

export default function PlanosPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const isPremium = user?.is_premium || user?.is_founder;

  const handleCheckout = (plan) => {
    if (!plan.stripeLink) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    toast.info(`Redirecionando para pagamento: ${plan.name}`);
    window.open(plan.stripeLink, '_blank');
  };

  return (
    <PageTransition>
      <div className="max-w-6xl mx-auto py-8 px-4" data-testid="planos-page">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Crown className="h-7 w-7 text-primary" strokeWidth={1.5} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2">Planos U-Clean</h1>
          <p className="text-sm text-muted-foreground max-w-lg mx-auto">
            Apoie a U-Clean e desbloqueie recursos exclusivos para sua jornada. A rede social é e sempre será gratuita.
          </p>
          {isPremium && (
            <Badge className="mt-3 bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300">
              <Crown className="h-3 w-3 mr-1" /> Você já é Premium
            </Badge>
          )}
        </div>

        {/* Plans Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-10">
          {PLANS.map((plan, i) => (
            <FadeInItem key={plan.id} index={i}>
              <PlanCard plan={plan} onCheckout={handleCheckout} isFree={!plan.stripeLink} />
            </FadeInItem>
          ))}
        </div>

        {/* VSA Wallet CTA */}
        <Card className="rounded-2xl border-border/40 shadow-sm max-w-2xl mx-auto" data-testid="wallet-cta">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/30">
              <Wallet className="h-6 w-6 text-blue-600 dark:text-blue-400" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm">Carteira VSA</h3>
              <p className="text-xs text-muted-foreground">Gerencie seus tokens AWA e NFTs adquiridos</p>
            </div>
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => navigate('/carteira')} data-testid="go-wallet-btn">
              Acessar
            </Button>
          </CardContent>
        </Card>

        {/* Trust */}
        <p className="text-center text-xs text-muted-foreground/50 mt-8">
          Pagamento 100% seguro via Stripe · Criptografia SSL · Todas as bandeiras
        </p>
      </div>
    </PageTransition>
  );
}

function PlanCard({ plan, onCheckout, isFree }) {
  const Icon = plan.icon;
  return (
    <Card className={`rounded-2xl border shadow-sm hover:shadow-md transition-all relative overflow-hidden ${plan.border} ${plan.popular ? 'ring-2 ring-primary' : ''}`} data-testid={`plan-card-${plan.id}`}>
      {plan.popular && (
        <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-[10px] font-bold px-3 py-1 rounded-bl-xl">
          RECOMENDADO
        </div>
      )}
      <CardContent className="p-5 flex flex-col h-full">
        {/* Icon + Name */}
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-10 h-10 rounded-xl ${plan.bg} flex items-center justify-center`}>
            <Icon className={`h-5 w-5 ${plan.color}`} strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="font-bold text-sm">{plan.name}</h3>
            <p className="text-[10px] text-muted-foreground">{plan.subtitle}</p>
          </div>
        </div>

        {/* Price */}
        <div className="mb-3">
          <span className="text-xl font-bold">{plan.price}</span>
          {plan.period && <span className="text-xs text-muted-foreground">{plan.period}</span>}
          {plan.originalPrice && (
            <span className="ml-1.5 text-xs text-muted-foreground line-through">{plan.originalPrice}</span>
          )}
        </div>

        <p className="text-xs text-muted-foreground mb-3">{plan.description}</p>

        {/* NFT Badge */}
        {plan.nft && (
          <Badge variant="outline" className="mb-3 self-start text-[10px] gap-1 rounded-md border-emerald-300 text-emerald-700 dark:text-emerald-400 dark:border-emerald-700">
            <TreeDeciduous className="h-3 w-3" /> NFT na Wallet
          </Badge>
        )}

        {/* Benefits */}
        <ul className="space-y-1.5 flex-1 mb-4">
          {plan.benefits.map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-xs">
              <Check className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${plan.color}`} />
              <span className="text-muted-foreground">{b}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        {isFree ? (
          <Button variant="outline" className="w-full rounded-xl text-xs h-9" disabled>
            Plano Atual
          </Button>
        ) : (
          <Button
            onClick={() => onCheckout(plan)}
            className={`w-full rounded-xl text-xs h-9 ${plan.popular ? 'bg-primary hover:bg-primary/90' : ''}`}
            variant={plan.popular ? 'default' : 'outline'}
            data-testid={`subscribe-${plan.id}`}
          >
            Assinar
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
